import React, { createContext, useCallback, useContext, useState } from "react";
import { AlertCircle, CircleX } from "lucide-react";
import { Button } from "../button";
import CustomModal from "../modal/modal-core";

export type ModalType = "error" | "success" | "warning" | "info";

export interface ModalItem {
  id: string;
  type: ModalType;
  title: string;
  message: string;
  actionLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
}

interface ModalContextType {
  modal: ModalItem | null;
  addModal: (modal: Omit<ModalItem, "id">) => void;
  removeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within ModalProvider");
  return context;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modal, setModal] = useState<ModalItem | null>(null);
  const addModal = useCallback((next: Omit<ModalItem, "id">) => {
    setModal({ ...next, id: `${Date.now()}` });
  }, []);
  const removeModal = useCallback(() => setModal(null), []);

  return (
    <ModalContext.Provider value={{ modal, addModal, removeModal }}>
      {children}
      <GlobalModal />
    </ModalContext.Provider>
  );
};

const GlobalModal: React.FC = () => {
  const { modal, removeModal } = useModal();
  const isError = modal?.type === "error";
  const hasCancelAction = Boolean(modal?.cancelLabel);

  return (
    <CustomModal
      isOpen={Boolean(modal)}
      onClose={removeModal}
      maxWidth="xs"
      showCloseButton
      paperClassName="!rounded-[16px]"
      contentClassName="px-6 pb-5 pt-5"
    >
      <div
        role={isError ? "alert" : undefined}
        className="text-center"
      >
        <div className="flex flex-row items-center">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
            {isError ? (
              <AlertCircle
                aria-hidden="true"
                className="h-8 w-8"
                strokeWidth={2.5}
              />
            ) : (
              <CircleX
                aria-hidden="true"
                className="h-8 w-8"
                strokeWidth={2.5}
              />
            )}
          </div>
          <div className="ml-4 min-w-0 flex-1 text-center">
            <h2 className="text-base font-medium leading-6 text-slate-800">
              {modal?.title ?? "Lỗi"}
            </h2>
            <p className="mt-1.5 text-sm leading-5 text-slate-500">{modal?.message}</p>
          </div>
        </div>
        <div className="mt-7 flex justify-center gap-3">
          {hasCancelAction ? (
            <Button
              type="button"
              variant="outline"
              onClick={removeModal}
              className="min-w-[96px]"
            >
              {modal?.cancelLabel}
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              modal?.onConfirm?.();
              removeModal();
            }}
            className="min-w-[96px] bg-[#05b99f] text-white shadow-none hover:bg-[#04a88f]"
          >
            {modal?.actionLabel ?? "OK"}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};
